import { Chip, type ChipTone } from "@nt/ui/chip";

import { Tooltip, TooltipContent, TooltipTrigger } from "@nt/ui/tooltip";

import { useWindowDays } from "@/lib/queries";

/** What each referral and fee status means, in the partner's words. */
export const STATUS: Record<
  string,
  { label: string; tone: ChipTone; tip: string }
> = {
  signed_up: {
    label: "Signed up",
    tone: "surface",
    // tipOf names the program's real window; this is the text without it.
    tip: "The customer created an account. You earn when they buy within your attribution window after the click.",
  },
  pending: {
    label: "Pending",
    tone: "info",
    tip: "The customer paid. The fee waits out the hold period before it becomes payable.",
  },
  on_hold: {
    label: "On hold",
    tone: "warning",
    tip: "We are reviewing this fee. Nothing is needed from you.",
  },
  payable: {
    label: "Payable",
    tone: "success",
    tip: "Ready for the next monthly payout, once your balance reaches the minimum.",
  },
  paid: {
    label: "Paid",
    tone: "success",
    tip: "Included in a payout we sent.",
  },
  reversed: {
    label: "Reversed",
    tone: "danger",
    tip: "The customer was refunded or disputed the payment, so the fee was taken back.",
  },
  rejected: {
    label: "Rejected",
    tone: "danger",
    tip: "This fee does not qualify under the program terms.",
  },
  not_eligible: {
    label: "No fee",
    tone: "surface",
    tip: "This order does not earn a fee, for example a $0 order or an order paid offline.",
  },
  voided: {
    label: "Voided",
    tone: "surface",
    tip: "This referral no longer counts, for example when your account was closed.",
  },
};

/** The status entry, falling back to Pending for a key this build does not know. */
export const statusOf = (status: string) => STATUS[status] ?? STATUS.pending!;

/** The tooltip text; a signed-up customer's names the attribution window when it is known. */
export function tipOf(status: string, windowDays: number | null): string {
  if (status === "signed_up" && windowDays !== null) {
    const span = `${windowDays} ${windowDays === 1 ? "day" : "days"}`;
    return `The customer created an account. You earn when they buy within ${span} of the click.`;
  }
  return statusOf(status).tip;
}

/**
 * A status chip whose meaning shows in a tooltip on hover or keyboard focus.
 * The meaning is also in the markup for screen readers (and server renders).
 */
export function StatusChip({
  status,
  windowDays,
}: {
  status: string;
  /** The attribution window; read from the program settings when omitted. */
  windowDays?: number | null;
}) {
  const programWindow = useWindowDays();
  const s = statusOf(status);
  const tip = tipOf(status, windowDays ?? programWindow);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className="inline-flex rounded-pill outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          />
        }
      >
        <Chip tone={s.tone}>{s.label}</Chip>
        <span className="sr-only">: {tip}</span>
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}
